import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Operation, OperationSuccessState } from '@x/common/operation';
import { AddressService } from '@x/ecommerce-shop/app/core/address/services/address.service';
import { IShopAddressInput, IShopAuthUser, IShopUserAddress } from '@x/ecommerce/shop-client';
import { PointInput } from '@x/schemas/ecommerce';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

export type AddressBookAction = 'edit' | 'delete' | 'create';

export interface UpdateAddressEvent {
  addressInput: IShopAddressInput;
  addressId: number;
  hash: string;
  recipientHash?: string | null | undefined;
}

@Component({
  selector: 'x-address-book',
  templateUrl: './address-book.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'x-address-book' },
})
export class AddressBookComponent {
  _addressTooltipBackup: string;

  @Input() user: IShopAuthUser | null | undefined;
  @Input() addresses: IShopUserAddress[] = [];
  @Input() hasNotification: boolean = false;
  @Input() selectedAddressHash: string | undefined | null;
  @Input() selectedAddressId: number | undefined | null;
  @Input() addressTooltip: string = '';
  @Input() defaultCoordinates: PointInput = { lat: -33.822505, lng: 18.929985 };
  @Input() restrictedRegions: number[] = [];
  @Input() heading: string;
  @Input() defaultCountry: string | undefined | null;
  @Input() radioButtons: boolean = true;
  @Input() addressActions: AddressBookAction[] = ['edit', 'delete', 'create'];
  @Input() disableAddressActions: boolean = false;
  @Input() origin: 'My Account' | 'Checkout';

  @Output() addressSelected = new EventEmitter<IShopUserAddress>();
  @Output() addressDeleted = new EventEmitter<IShopUserAddress>();
  @Output() addressUpdated = new EventEmitter<UpdateAddressEvent>();
  @Output() addressCreated = new EventEmitter<IShopAddressInput>();

  operation$: Observable<Operation> = of(Operation.create());

  constructor(private addressService: AddressService) {}

  createAddress(user: IShopAuthUser) {
    this.operation$ = this.addressService
      .validAddress(
        {
          sheetTitle: 'Add address',
          user,
          defaultCoordinates: this.defaultCoordinates,
          ...(this.defaultCountry && { defaultCountry: this.defaultCountry }),
          origin: this.origin,
        },
        this.restrictedRegions,
      )
      .pipe(
        tap(({ state }) => {
          if (state instanceof OperationSuccessState && state.data) {
            this.addressCreated.emit(state.data);
          }
        }),
      );
  }

  updateAddress(address: IShopUserAddress, user: IShopAuthUser, $event: MouseEvent) {
    $event.stopPropagation();

    const { firstName, lastName, phoneNumber } = address;

    this.operation$ = this.addressService
      .validAddress(
        {
          sheetTitle: 'Update Address',
          address: this.addressService.getAddressInputFromShopAddress(address),
          user: {
            firstName: firstName ?? user.firstName,
            lastName: lastName ?? user.lastName,
            phone: phoneNumber ?? user.phone,
          },
          defaultCoordinates: address.coordinates,
          origin: this.origin,
        },
        this.restrictedRegions,
      )
      .pipe(
        tap(({ state }) => {
          if (state instanceof OperationSuccessState && state.data) {
            this.addressUpdated.emit({
              addressInput: state.data,
              addressId: address.id,
              hash: address.hash,
              recipientHash: address.recipientHash,
            });
          }
        }),
      );
  }

  deleteAddress(address: IShopUserAddress, $event: MouseEvent) {
    $event.stopPropagation();
    this.addressDeleted.emit(address);
  }

  selectAddress(address: IShopUserAddress) {
    this.addressSelected.emit(address);
  }

  hideTooltip() {
    this._addressTooltipBackup = this.addressTooltip;
    this.addressTooltip = '';
  }

  showTooltip() {
    this.addressTooltip = this._addressTooltipBackup;
  }
}
